import React, {Component, ReactElement, ReactNode} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {PageTitle} from '../ownershipTransfers/common/NatOwnershipTransfersCommon'
import {DisasterPreventionInfo} from './repository/NatGetDisasterPreventionInfoList'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {Colors, FontSize} from '../../../styledComponents/Constants'
import {formatDateSlashSeparator} from '../../../formatters/formatDate'
import {CardActionArea} from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import NatDisasterRepo from './repository/NatDisasterRepo'
import EventEmitterExResident from '../../../components/shared/pageBaseComponents/EventEmitterExResident'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {PlainBackLink} from '../../../styledComponents/Links'
import {DisplayItemDividerForPageTop} from '../../../styledComponents/DisplayItem'
import {BlockSpacer, currentMedia} from '../../../styledComponents/ResponsiveConvenience'
import {DISASTER_RELEASE_SETTING, HAVE_OR_NOT} from '../../../CodeMaster'
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {Location} from 'react-router-dom'


interface Props {
    condominiumCd: string
    disasterRepo: NatDisasterRepo
    eventEmitter: EventEmitterExResident
    location: Location
}

interface State {
    disasterInfoList: DisasterPreventionInfo[],
    isLoadCompleted: boolean
}

class NatResidentDisasterPreventionsPage extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)
        this.state = {
            disasterInfoList: [],
            isLoadCompleted: false
        }

        this.onClickDisasterInfo = this.onClickDisasterInfo.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const {condominiumCd, disasterRepo, eventEmitter} = this.props

            const response = await this.props.asyncCallFunction(
                disasterRepo.getDisasterInfoList.bind(disasterRepo,condominiumCd),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            if (response?.details) {
                this.setState({
                    disasterInfoList: response.details.disasterInfo
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    onClickDisasterInfo(disasterPreventionId: number): void {
        const {condominiumCd} = this.props
        this.props.navigate(`/resident/condominiums/${condominiumCd}/disasters/preventions/${disasterPreventionId}`)
    }

    renderDisasterInfoList(): ReactNode {
        return (
            <MainContainer>
                {this.state.disasterInfoList.map((disasterInfo) => (
                    <DisasterInfoArea key={disasterInfo.disasterPreventionId}
                                      onClick={() => this.onClickDisasterInfo(disasterInfo.disasterPreventionId)}>
                        <DisasterArea>
                            <DisasterHeader>
                                <UpdateDateTime>
                                    {formatDateSlashSeparator(disasterInfo.updateDatetime)}
                                </UpdateDateTime>
                                {(disasterInfo.releaseSetting === DISASTER_RELEASE_SETTING.DISASTER.value) &&
                                    <ReleaseSettingLabel>
                                        {DISASTER_RELEASE_SETTING
                                            .codeToEnumConstants(disasterInfo.releaseSetting).label
                                        }
                                    </ReleaseSettingLabel>
                                }
                                {(disasterInfo.attachmentFlag === HAVE_OR_NOT.HAVE.value) &&
                                    <StyledAttachFileIcon/>
                                }
                            </DisasterHeader>
                            <DisasterTitle>
                                {disasterInfo.title}
                            </DisasterTitle>
                        </DisasterArea>
                    </DisasterInfoArea>
                ))}
            </MainContainer>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return <>
            <PlainBackLink to={`/resident/condominiums/${this.props.condominiumCd}/disasters/prevention/top`}>
                戻る
            </PlainBackLink>
            <BlockSpacer height={'16px'}/>
            <DisplayItemDividerForPageTop/>
            <BlockSpacer height={'30px'}/>
            <PageTitle>防災情報一覧</PageTitle>
            <BlockSpacer height={'20px'}/>
            {(this.state.disasterInfoList.length === 0) ?
                <NoDisasterInfo>防災情報はありません</NoDisasterInfo>
                :
                this.renderDisasterInfoList()
            }
        </>
    }
}


export default createResidentWithErrorHandlingPage(NatResidentDisasterPreventionsPage)

const NoDisasterInfo: StyledDiv = styled.div`
    color: ${Colors.Body};
    text-align: left;
`
const MainContainer: StyledDiv = styled.div`
  background-color: ${Colors.Surface};

  & > button:first-child > div {
    border-top: none;
  }
    ${currentMedia.lessThan('sp')`
    margin: 0 -15px;
`}
`

const DisasterInfoArea = styled(CardActionArea)`
  &.MuiCardActionArea-root {
    padding: 0 15px;
  }
`

const DisasterArea: StyledDiv = styled.div`
  border-top: 1px solid ${Colors.AccentLight};
  padding-top: 7px;
  padding-bottom: 9px;
`

const DisasterHeader: StyledDiv = styled.div`
  display: flex;
  height: 24px;
  align-items: center;
`

const UpdateDateTime: StyledDiv = styled.div`
  width: 63px;
  font-size: ${FontSize.Small};
  color: ${Colors.AccentDark};
`

const DisasterTitle: StyledDiv = styled.div`
  margin-top: 2px;
  font-size: ${FontSize.Middle};
  color: ${Colors.Body};
  line-height: 1.5;
`

const ReleaseSettingLabel = styled.div`
    background-color: ${Colors.Disaster};
    color: ${Colors.Surface};
    text-align: center;
    font-size: 8px;
    line-height: 8px;
    padding: 4px 3px;
    margin-left: 5px;
`

const StyledAttachFileIcon = withStyles({
    root: {
        fontSize: '16px',
    }
})(AttachFileIcon)